import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DropResult
} from '@hello-pangea/dnd';
import { Button, IconButton, Stack, type Theme, Typography } from '@mui/material';
import _get from 'lodash/get';
import { mergeSx } from 'merge-sx';

import { FullTalentQuestionDTOType } from '@/api/generated';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import DragIcon from '@/assets/icons/drag-vertical.svg?react';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import { useDirection } from '@/hooks/useDirection';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { borders, transforms, transitions } from '@/theme/constants';
import typography from '@/theme/typography';

import { DEFAULT_LANGUAGE_CODE, OTHER_OPTION_ID } from './constants';
import MultiOptionSymbol from './MultiOptionSymbol';
import { hasOther, skipOther } from './utils';

interface TalentRegistrationFormQuestionOptionProps {
  activeLanguage: string;
  questionId: string;
  options?: string[];
  isOtherEnabled: boolean;
  questionType: FullTalentQuestionDTOType;
  onAddQuestionOption: (questionId: string, isOther?: boolean) => void;
  onRemoveQuestionOption: (questionId: string, optionId: string) => void;
  onUpdateOptions: (options: string[]) => void;
}

const TalentRegistrationFormMultiOption = ({
  activeLanguage,
  questionId,
  options,
  isOtherEnabled,
  questionType,
  onAddQuestionOption,
  onRemoveQuestionOption,
  onUpdateOptions
}: TalentRegistrationFormQuestionOptionProps) => {
  const { t } = useTranslation('settings');
  const { directionalSpacing, applyDirectionalStyles } = useDirection();
  const { watch } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();
  const renderOptions = useMemo(() => skipOther(options!), [options]);
  const containsOther = useMemo(() => hasOther(options!), [options]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || !renderOptions) return;
      const updatedOptions = [...renderOptions];
      const [removed] = updatedOptions.splice(result.source.index, 1);
      updatedOptions.splice(result.destination.index, 0, removed);
      onUpdateOptions(containsOther ? [...updatedOptions, OTHER_OPTION_ID] : updatedOptions);
    },
    [renderOptions, containsOther, onUpdateOptions]
  );

  const translations = watch(getPrefixedFieldName('translations'));

  const getLabelPlaceholder = useCallback(
    (optionId: string) => {
      const otherLanguages = Object.keys(translations || {}).filter(
        lang => lang !== DEFAULT_LANGUAGE_CODE && lang !== activeLanguage
      );
      const existingDefaultTranslation = _get(translations, `${DEFAULT_LANGUAGE_CODE}.${questionId}.optionLabels`);
      const fallbackTranslation = otherLanguages
        .flatMap(lang =>
          Object.entries(
            (_get(translations, `${lang}.${questionId}.optionLabels`) || {}) as { [key: string]: string }
          ).map(([key, value]) => ({ optionId: key, translation: value }))
        )
        .reduce(
          (optionLabels: { [key: string]: string }, entry: { optionId: string; translation: string }) => ({
            ...optionLabels,
            [entry.optionId]: optionLabels?.[entry.optionId] || entry.translation
          }),
          {} as { [key: string]: string }
        );

      return (
        existingDefaultTranslation?.[optionId] ||
        fallbackTranslation?.[optionId] ||
        t(`orgSettings.talentRegistrationForm.${optionId !== OTHER_OPTION_ID ? 'optionPlaceholder' : 'other'}`)
      );
    },
    [activeLanguage, questionId, t, translations]
  );

  const renderRow = useCallback(
    ({
      provided,
      snapshot,
      isDraggingContext,
      optionId,
      index
    }: {
      provided?: DraggableProvided;
      snapshot?: DraggableStateSnapshot;
      isDraggingContext: boolean;
      index: number;
      optionId: string;
    }) => (
      <Stack
        {...provided?.draggableProps}
        key={optionId}
        direction="row"
        alignItems="center"
        mb={1}
        ref={provided?.innerRef}
        sx={mergeSx<Theme>(
          {
            userSelect: 'none',
            opacity: snapshot?.isDragging ? 0.6 : 1,
            bgcolor: snapshot?.isDragging ? 'primary.100' : 'background.default',
            '& .MultiOption-dragHandle': {
              opacity: 0,
              transition: transitions.opacity
            },
            '&:hover .MultiOption-dragHandle': {
              opacity: 1
            }
          },
          provided && applyDirectionalStyles(-2, 0, 'ml', 'mr'),
          !snapshot?.isDragging &&
            isDraggingContext && {
              '&:hover .MultiOption-dragHandle': {
                opacity: 0
              }
            },
          snapshot?.isDragging && {
            '& .MultiOption-dragHandle': {
              opacity: 1
            }
          }
        )}
      >
        {provided && (
          <Stack {...provided?.dragHandleProps} justifyContent="center">
            <DragIcon className="MultiOption-dragHandle" />
          </Stack>
        )}
        <MultiOptionSymbol questionType={questionType} index={index} />
        <Field
          component={TextInput}
          name={`translations.${activeLanguage}.${questionId}.optionLabels.${optionId}`}
          innerProps={{
            sx: mergeSx<Theme>(
              {
                flexGrow: 1,
                p: 0,
                mb: 0,
                '& .MuiInputBase-root .MuiInputBase-input': {
                  ...typography.body2,
                  p: directionalSpacing(0.75, 0.5, 0.75, 0.5)
                },
                '& .MuiInputBase-root::before': { display: 'none' },
                '& .MuiInputBase-root::after': {
                  borderBottom: borders.half,
                  borderBottomColor: 'text.primary'
                },
                '&:hover .MuiInputBase-root::after, & .MuiInputBase-root.Mui-error::after': {
                  transform: transforms.compose(transforms.scale100X, transforms.shiftX(0))
                },
                '& .MuiInputBase-root.Mui-error::after': {
                  borderBottomColor: 'red.main'
                },
                '& .MuiInputBase-root.Mui-focused': { boxShadow: 'none' }
              },
              !snapshot?.isDragging &&
                isDraggingContext && {
                  '& .MuiInputBase-root::after': {
                    display: 'none'
                  }
                },
              snapshot?.isDragging && {
                '& .MuiInputBase-root': { bgcolor: 'transparent' },
                '& .MuiInputBase-root::after': {
                  transform: transforms.compose(transforms.scale100X, transforms.shiftX(0))
                },
                '& .DeleteIcon-root': {
                  display: 'none'
                }
              }
            ),
            placeholder: getLabelPlaceholder(optionId),
            variant: 'standard',
            InputProps: {
              readOnly: optionId === OTHER_OPTION_ID
            }
          }}
        />
        <IconButton
          title={t('orgSettings.talentRegistrationForm.delete')}
          className="DeleteIcon-root"
          onClick={() => onRemoveQuestionOption(questionId, optionId)}
          sx={mergeSx<Theme>(
            { p: 0 },
            snapshot?.isDragging && {
              display: 'none'
            }
          )}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
    ),
    [
      activeLanguage,
      applyDirectionalStyles,
      directionalSpacing,
      getLabelPlaceholder,
      onRemoveQuestionOption,
      questionId,
      questionType,
      t
    ]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={questionId}>
        {(provided, multiOptionSnapshot) => (
          <Stack key={questionId} py={2} mb={2} ref={provided.innerRef} {...provided.droppableProps}>
            {renderOptions?.map((optionId: string, index: number) => (
              <Draggable key={optionId} draggableId={optionId} index={index}>
                {(provided, snapshot) =>
                  renderRow({
                    provided,
                    snapshot,
                    isDraggingContext: multiOptionSnapshot.isDraggingOver,
                    optionId,
                    index
                  })
                }
              </Draggable>
            ))}
            {provided.placeholder}
            {containsOther &&
              renderRow({
                isDraggingContext: multiOptionSnapshot.isDraggingOver,
                index: renderOptions?.length || 0,
                optionId: OTHER_OPTION_ID
              })}
            <Stack
              direction="row"
              alignItems={questionType === FullTalentQuestionDTOType.SELECT ? 'baseline' : 'center'}
              sx={{
                '& .MuiButtonBase-root': {
                  py: 0.5,
                  px: 0.5
                }
              }}
            >
              <MultiOptionSymbol questionType={questionType} index={options?.length || 0} />
              <Button onClick={() => onAddQuestionOption(questionId, false)} sx={{ color: 'grey.700' }}>
                {t('orgSettings.talentRegistrationForm.addOption')}
              </Button>
              {isOtherEnabled && (
                <>
                  <Typography>{t('orgSettings.talentRegistrationForm.or')}</Typography>
                  <Button onClick={() => onAddQuestionOption(questionId, true)} sx={{ textTransform: 'none' }}>
                    {t('orgSettings.talentRegistrationForm.addOther')}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default TalentRegistrationFormMultiOption;
